/**
 * Druck der Lagebeschreibung: einseitiges DIN-A4-Infoblatt im
 * Kartenwerkzeug-PDF-Design (CadetBlue/Anthrazit/Off-white, Helvetica).
 * Aus DescriptionDisplay extrahiert, damit die Übersicht drucken kann.
 */

import type { LagepromptResponse, LageSection } from '../../types/api'

const PRINT_STYLES = `
  @page { size: A4 portrait; margin: 16.5mm 17.5mm 8mm 17.5mm; }
  * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  html { height: 100%; }
  body { font-family: Helvetica, Arial, sans-serif; color: #212121; margin: 0;
         font-size: 7.5pt; line-height: 1.35;
         display: flex; flex-direction: column; height: 100%; }
  .flex-gap { flex: 1 1 auto; min-height: 4mm; }
  h1 { font-size: 11pt; margin: 0 0 1pt; }
  .meta { font-size: 7pt; color: #212121; }
  .titleblock { margin-bottom: 6pt; }
  h2 { font-size: 9pt; margin: 7pt 0 3pt; color: #212121; }
  .grid { display: grid; grid-template-columns: 1fr 1fr; column-gap: 8pt; row-gap: 5pt; }
  .box { break-inside: avoid; }
  .box.wide { grid-column: span 2; }
  .box.fazit { background: #F7F7F5; padding: 4pt 5pt; border-left: 2pt solid #5F9EA0; }
  .box h3 { font-size: 8pt; margin: 0 0 1.5pt; color: #5F9EA0; text-transform: uppercase;
            letter-spacing: 0.3pt; font-weight: bold; }
  .box p { margin: 0; text-align: justify; }
  .rating-row { display: flex; justify-content: space-between; align-items: baseline; margin-top: 2.5pt; }
  .rating-label { font-size: 7pt; color: #5F9EA0; font-weight: bold; }
  .rating-value { font-size: 7.5pt; font-weight: bold; }
  .score { font-size: 6.5pt; color: #888; margin-left: 2.5pt; font-weight: normal; }
  .bar { width: 100%; height: 2.5pt; background: #ECECEA; margin-top: 1.5pt; }
  .fill { height: 2.5pt; }
  .chips { margin-top: 2pt; font-size: 6.5pt; color: #212121; }
  .chip { display: inline-block; margin: 0 6pt 1pt 0; }
  .chip b { color: #5F9EA0; font-weight: bold; }
  .sources { font-size: 6pt; color: #666; text-align: right; margin-top: 6pt; }
  footer { margin-top: 4pt;
           border-top: 0.5pt solid #212121; padding-top: 2.5pt;
           display: flex; justify-content: space-between; align-items: flex-end;
           font-size: 8pt; color: #212121; }
  footer .context { line-height: 1.4; }
  footer .context .label { display: block; }
  footer .page { white-space: nowrap; }
`

/**
 * Kürzt einen Text für den Druck an Satzgrenzen (Leittexte können lang sein).
 * Fazit-Boxen werden nie gekürzt.
 */
function clampSentences(text: string, maxChars: number): string {
  if (text.length <= maxChars) return text
  const sentences = text.split(/(?<=[.!?])\s+/)
  let out = ''
  for (const sentence of sentences) {
    if (out && `${out} ${sentence}`.length > maxChars) break
    out = out ? `${out} ${sentence}` : sentence
  }
  return out.length < text.trim().length ? `${out} …` : out
}

const PRINT_BAR_COLORS: Array<[number, string]> = [
  [70, '#22c55e'],
  [50, '#84cc16'],
  [40, '#eab308'],
  [25, '#f97316'],
  [0, '#ef4444'],
]

function printBarColor(score: number): string {
  for (const [threshold, color] of PRINT_BAR_COLORS) {
    if (score >= threshold) return color
  }
  return '#ef4444'
}

function escapeHtml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

/** Öffnet ein Druckfenster mit dem A4-Infoblatt zur Lagebeschreibung. */
export function printLagebeschreibung(data: LagepromptResponse): void {
  const desc = data.description

  /** isIntro = erster Bereich der Ebene (volle Breite, längerer Auszug) */
  const boxHtml = (s: LageSection, isIntro: boolean) => {
    const isFazit = s.key === 'fazit'
    const wide = isIntro || isFazit
    const maxChars = isFazit ? Infinity : isIntro ? 1100 : 650
    const text = clampSentences(s.text, maxChars)
    const rating = s.rating
      ? `<div class="rating-row">
           <span class="rating-label">Rating</span>
           <span class="rating-value">„${s.rating.term}"<span class="score">${s.rating.score.toFixed(1)}</span></span>
         </div>
         <div class="bar"><div class="fill" style="width:${Math.max(2, Math.min(100, s.rating.score))}%;background:${printBarColor(s.rating.score)}"></div></div>`
      : ''
    const subs = s.sub_ratings ?? []
    const chips = subs.length
      ? `<div class="chips">${subs
          .map((x) => `<span class="chip"><b>${escapeHtml(x.label)}:</b> „${x.term}"</span>`)
          .join('')}</div>`
      : ''
    return `<div class="box ${wide ? 'wide' : ''} ${isFazit ? 'fazit' : ''}">
      <h3>${escapeHtml(s.title)}</h3><p>${escapeHtml(text)}</p>${rating}${chips}</div>`
  }

  const gridHtml = (heading: string, sections: LageSection[]) => {
    // Bereiche ohne Text (z. B. leeres Template) erzeugen keine leere Box.
    const visible = sections.filter((s) => (s.text ?? '').trim().length > 0)
    if (visible.length === 0) return ''
    const firstThemeKey = visible.find((s) => s.key !== 'fazit')?.key
    return `<h2>${escapeHtml(heading)}</h2><div class="grid">${visible
      .map((s) => boxHtml(s, s.key === firstThemeKey))
      .join('')}</div>`
  }

  const html = `<!DOCTYPE html><html lang="de"><head><meta charset="utf-8">
    <title>Lagebeschreibung ${escapeHtml(data.municipality_name)}</title>
    <style>${PRINT_STYLES}</style></head><body>
    <div class="titleblock">
      <h1>Lagebeschreibung</h1>
      <div class="meta">Erstellt am ${new Date().toLocaleDateString('de-DE')} mit Lagewerkzeug</div>
    </div>
    ${gridHtml(desc.makrolage_heading, desc.makro_sections)}
    <div class="flex-gap"></div>
    ${gridHtml(desc.mikrolage_heading, desc.mikro_sections)}
    <div class="sources">Ratings: Quantilsränge über alle Kreise bzw. Gemeinden |
      Daten: © Regionalstatistik (Destatis), INKAR (BBSR), Prognos Zukunftsatlas, MB-Research, BfN-Landschaftssteckbriefe, GV100 (Destatis)</div>
    <footer>
      <div class="context">
        <span class="label">Lagebeschreibung zu:</span>
        <span>${escapeHtml(data.municipality_name)}, ${escapeHtml(data.county_name ?? '')} (AGS ${data.ags})</span>
      </div>
      <div class="page">Seite 1 von 1</div>
    </footer>
    </body></html>`

  const win = window.open('', '_blank', 'width=900,height=700')
  if (!win) return
  win.document.write(html)
  win.document.close()
  win.focus()
  win.print()
}
